import { TableFilter } from 'components/Table';
import strings from 'constants/index';

import { getFilterConditionByName } from './pagination.utils';

export enum FilterConditionKey {
  Equals = 'equals',
  In = 'in',
  NotIn = 'nin',
  Contains = 'contains',
  StartsWith = 'startsWith',
  FromDate = 'fromDate',
  ToDate = 'toDate',
}

type FilterProps = {
  type: 'date' | 'datetime' | 'enum' | 'number' | 'text';
  values?: string[];
  disablePastDatePicker?: boolean;
  excludeConditions?: FilterConditionKey[]; // New optional prop
  // Opt-in extra conditions to append to the defaults for the given type.
  // Use for conditions that should only appear on specific columns (e.g. 'in'/'nin' on text columns).
  additionalConditions?: FilterConditionKey[];
};

const shouldInclude = (key: string, excludeConditions?: FilterConditionKey[]) =>
  !excludeConditions?.includes(key as FilterConditionKey);

const TEXT_ADDITIONAL_CONDITION_LABELS: Partial<Record<FilterConditionKey, string>> = {
  [FilterConditionKey.In]: 'in - comma separated values',
  [FilterConditionKey.Equals]: 'equals',
  [FilterConditionKey.Contains]: 'contains',
  [FilterConditionKey.StartsWith]: 'startsWith',
};

export const getDefaultFiltersForType = ({
  type,
  values = [],
  disablePastDatePicker = false,
  excludeConditions,
  additionalConditions,
}: FilterProps): TableFilter[] => {
  switch (type) {
    case 'number': {
      const conditions = [
        { key: FilterConditionKey.Equals, label: 'equals' },
        { key: FilterConditionKey.In, label: 'in - comma separated numbers' },
        { key: FilterConditionKey.NotIn, label: 'not in - comma separated numbers' },
      ].filter(c => shouldInclude(c.key, excludeConditions));
      return [{ type: 'number', conditions }];
    }
    case 'text': {
      const baseConditions = [
        { key: FilterConditionKey.Contains, label: 'contains' },
        { key: FilterConditionKey.StartsWith, label: 'startsWith' },
        { key: FilterConditionKey.Equals, label: 'equals' },
      ];
      const extraConditions = (additionalConditions ?? [])
        .filter(key => !baseConditions.some(c => c.key === key))
        .map(key => ({
          key,
          label: TEXT_ADDITIONAL_CONDITION_LABELS[key] ?? key,
        }));
      const conditions = [...baseConditions, ...extraConditions].filter(c =>
        shouldInclude(c.key, excludeConditions),
      );
      return [{ type: 'text', conditions, label: 'includes' }];
    }
    case 'date': {
      const fromKey = getFilterConditionByName(FilterConditionKey.FromDate);
      const toKey = getFilterConditionByName(FilterConditionKey.ToDate);
      return [
        {
          type: 'date',
          conditions: shouldInclude(FilterConditionKey.FromDate, excludeConditions)
            ? [
                {
                  key: fromKey,
                  restrictionTargetKey: toKey,
                  restrictionType: 'max',
                  disablePast: disablePastDatePicker,
                },
              ]
            : [],
          label: 'From',
        },
        {
          type: 'date',
          conditions: shouldInclude(FilterConditionKey.ToDate, excludeConditions)
            ? [
                {
                  key: toKey,
                  restrictionTargetKey: fromKey,
                  restrictionType: 'min',
                  disablePast: disablePastDatePicker,
                },
              ]
            : [],
          label: 'To',
        },
      ];
    }
    case 'datetime': {
      const fromKey = getFilterConditionByName(FilterConditionKey.FromDate);
      const toKey = getFilterConditionByName(FilterConditionKey.ToDate);
      return [
        {
          type: 'datetime',
          conditions: shouldInclude(FilterConditionKey.FromDate, excludeConditions)
            ? [
                {
                  key: fromKey,
                  restrictionTargetKey: toKey,
                  restrictionType: 'max',
                  disablePast: disablePastDatePicker,
                },
              ]
            : [],
          label: strings.from,
        },
        {
          type: 'datetime',
          conditions: shouldInclude(FilterConditionKey.ToDate, excludeConditions)
            ? [
                {
                  key: toKey,
                  restrictionTargetKey: fromKey,
                  restrictionType: 'min',
                  disablePast: disablePastDatePicker,
                },
              ]
            : [],
          label: strings.to,
        },
      ];
    }
    case 'enum': {
      const conditions = [{ key: FilterConditionKey.Equals }].filter(c =>
        shouldInclude(c.key, excludeConditions),
      );
      return [{ type: 'enum', values, conditions, label: 'is' }];
    }
    default:
      return [];
  }
};
